/**
 * src/modules/sb-dialogs.ts — okienka SellBase zamiast natywnych okienek przeglądarki (pkt 16).
 *
 * Właściciel (15.09.2026): „to potwierdzanie ogarnij modalem sellbase a nie takim amatorskim
 * okienkiem z chrome". Ten moduł zastępuje `confirm()`, `alert()` i `prompt()`:
 *
 *   showConfirm(tytuł, treść, opcje?) → Promise<boolean>
 *   showPrompt(tytuł, treść, opcje?)  → Promise<string | null>
 *   showAlert(tytuł, treść, opcje?)   → Promise<void>
 *
 * Dlaczego natywny `<dialog>` + `showModal()`, a nie `#modal-overlay`:
 *   1. `<dialog>` w trybie modalnym trafia do TOP-LAYER przeglądarki, więc pokazuje się nad
 *      wszystkim — także nad innymi `<dialog open>` (Katalog V2: `.cv2-dialog`, okno luster).
 *      Stary `showConfirm` renderował do `#modal-overlay`, a żaden z-index nie przebije
 *      top-layer — potwierdzenie wołane z takiego okna lądowało POD nim.
 *   2. Escape / klik w tło / „×" ZAWSZE rozwiązują obietnicę (stary helper wieszał ją
 *      w nieskończoność — przez co kod czekający na odpowiedź nigdy nie ruszał dalej).
 *   3. Zdarzenia pod przyciskami wpinamy od razu, nie przez `setTimeout(…, 50)`.
 *
 * Klawiatura: Enter = potwierdzenie (poza textarea i poza fokusem na „Anuluj"/„×", gdzie Enter
 * ma naturalne znaczenie tego przycisku), Escape = anulowanie. Fokus wraca tam, gdzie był.
 * Kilka okienek naraz jest OK — ostatnie otwarte leży na wierzchu (tak działa top-layer).
 *
 * WAŻNE dla migracji z `confirm()`: `showModal()` jest wołane SYNCHRONICZNIE (zanim wywołanie
 * odda sterowanie), a modalny `<dialog>` unieruchamia całą stronę pod spodem. Dzięki temu
 * `await showConfirm(...)` w handlerze klika chroni przed dublem tak samo, jak robił to
 * blokujący `confirm()` — drugi klik w ten sam przycisk po prostu nie dochodzi.
 */

import { escapeHtml } from './utils.ts';

export type SbDialogTone = 'default' | 'danger' | 'warning' | 'success';

export interface SbConfirmOptions {
  /** Napis na przycisku potwierdzenia (domyślnie i18n `common.confirm`). */
  okLabel?: string;
  /** Napis na przycisku anulowania (domyślnie i18n `common.cancel`). */
  cancelLabel?: string;
  /** Akcja nieodwracalna — czerwony przycisk, fokus startowo na „Anuluj". */
  danger?: boolean;
  /** Zgodność wstecz ze starym `showConfirm`: `okClass: 'danger'` = `danger: true`. */
  okClass?: string;
  /** Ton okienka; `danger` ma pierwszeństwo przed `tone`. */
  tone?: SbDialogTone;
  /** Wypunktowanie pod treścią, np. „NIE MA BYĆ: Allegro — HurtowniaXXL". */
  details?: string[];
  /** Przypis drobnym drukiem, np. „Konta bez zmian zostają jak są. Decyzję można cofnąć." */
  note?: string;
  /** Fokus startowo na „Anuluj” bez czerwonego tonu — pytanie, na które przypadkowy Enter nie może odpowiedzieć. */
  focusCancel?: boolean;
}

export interface SbPromptOptions extends SbConfirmOptions {
  /** Etykieta nad polem. */
  label?: string;
  /** Wartość początkowa (odpowiednik drugiego argumentu natywnego `prompt`). */
  value?: string;
  placeholder?: string;
  type?: 'text' | 'password' | 'number' | 'textarea' | 'email';
  /** Puste pole nie przejdzie — przycisk potwierdzenia jest zablokowany. */
  required?: boolean;
  maxlength?: number;
  min?: number;
  max?: number;
  step?: number;
  /** Własna walidacja: zwróć komunikat błędu albo `null` gdy wartość jest OK. */
  validate?: (value: string) => string | null;
}

export interface SbAlertOptions {
  okLabel?: string;
  tone?: SbDialogTone;
  details?: string[];
}

/** i18n z zabezpieczeniem: gdy klucza nie ma, `t()` oddaje sam klucz — wtedy bierzemy literał. */
function tr(key: string, fallback: string): string {
  const w = window as unknown as { __sbI18n?: { t?: (k: string, p?: unknown) => string } };
  const fn = w.__sbI18n && w.__sbI18n.t;
  if (typeof fn !== 'function') return fallback;
  try {
    const value = fn(key, fallback);
    return typeof value === 'string' && value && value !== key ? value : fallback;
  } catch (_e: unknown) {
    return fallback;
  }
}

function resolveTone(opts: { danger?: boolean; okClass?: string; tone?: SbDialogTone }): SbDialogTone {
  if (opts.danger || opts.okClass === 'danger') return 'danger';
  return opts.tone || 'default';
}

function detailsHtml(details?: string[] | null): string {
  if (!details || !details.length) return '';
  return (
    '<ul class="sb-dialog-details">' +
    details.map((line) => '<li>' + escapeHtml(line) + '</li>').join('') +
    '</ul>'
  );
}

interface ShellConfig {
  title: string;
  message: string;
  tone: SbDialogTone;
  okLabel: string;
  okI18nKey: string | null;
  cancelLabel: string | null;
  cancelI18nKey: string | null;
  details?: string[] | null;
  note?: string | null;
  fieldHtml?: string;
}

interface Shell {
  dlg: HTMLDialogElement;
  card: HTMLElement;
  body: HTMLElement;
  okBtn: HTMLButtonElement;
  cancelBtn: HTMLButtonElement | null;
  closeBtn: HTMLButtonElement;
}

let _seq = 0;
let _fieldSeq = 0;

function buildShell(cfg: ShellConfig): Shell {
  const id = 'sb-dialog-' + ++_seq;
  const dlg = document.createElement('dialog');
  dlg.className = 'sb-dialog';
  dlg.id = id;
  dlg.dataset.tone = cfg.tone;
  dlg.setAttribute('aria-labelledby', id + '-title');

  const okI18nAttr = cfg.okI18nKey ? ' data-i18n="' + cfg.okI18nKey + '"' : '';
  const cancelI18nAttr = cfg.cancelI18nKey ? ' data-i18n="' + cfg.cancelI18nKey + '"' : '';
  const cancelBtnHtml = cfg.cancelLabel === null
    ? ''
    : '<button type="button" class="btn-secondary" data-sb-dialog-cancel' + cancelI18nAttr + '>' +
      escapeHtml(cfg.cancelLabel) + '</button>';

  dlg.innerHTML =
    '<div class="sb-dialog-card" role="document">' +
      '<div class="sb-dialog-header">' +
        '<h2 class="sb-dialog-title" id="' + id + '-title">' + escapeHtml(cfg.title) + '</h2>' +
        '<button type="button" class="sb-dialog-close" data-sb-dialog-close aria-label="Zamknij">&times;</button>' +
      '</div>' +
      '<div class="sb-dialog-body">' +
        (cfg.message ? '<div class="sb-dialog-message">' + escapeHtml(cfg.message) + '</div>' : '') +
        detailsHtml(cfg.details) +
        (cfg.note ? '<div class="sb-dialog-note">' + escapeHtml(cfg.note) + '</div>' : '') +
        (cfg.fieldHtml || '') +
      '</div>' +
      '<div class="sb-dialog-footer">' +
        cancelBtnHtml +
        '<button type="button" class="btn-primary" data-sb-dialog-ok' + okI18nAttr + '>' +
          escapeHtml(cfg.okLabel) +
        '</button>' +
      '</div>' +
    '</div>';

  document.body.appendChild(dlg);
  return {
    dlg,
    card: dlg.querySelector('.sb-dialog-card') as HTMLElement,
    body: dlg.querySelector('.sb-dialog-body') as HTMLElement,
    okBtn: dlg.querySelector('[data-sb-dialog-ok]') as HTMLButtonElement,
    cancelBtn: dlg.querySelector('[data-sb-dialog-cancel]') as HTMLButtonElement | null,
    closeBtn: dlg.querySelector('[data-sb-dialog-close]') as HTMLButtonElement,
  };
}

interface RunConfig<T> {
  shell: Shell;
  /** Wynik przy Escape / kliku w tło / „×" / „Anuluj". */
  cancelValue: T;
  /** Wynik przy potwierdzeniu; `null` = nie wolno teraz potwierdzić (np. błąd walidacji). */
  okValue: () => { value: T } | null;
  /** Co dostaje fokus po otwarciu. */
  focusEl: () => HTMLElement | null;
}

/** Otwiera okienko, wpina zdarzenia i oddaje obietnicę rozwiązywaną dokładnie raz. */
function run<T>(cfg: RunConfig<T>): Promise<T> {
  const { dlg, card, okBtn, cancelBtn, closeBtn } = cfg.shell;
  const prevFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

  return new Promise<T>((resolve) => {
    let settled = false;
    let result: T = cfg.cancelValue;

    const cleanup = (): void => {
      dlg.remove();
      if (prevFocus && document.contains(prevFocus)) {
        try { prevFocus.focus(); } catch (_e: unknown) { /* element mógł zniknąć */ }
      }
      resolve(result);
    };

    const finish = (value: T): void => {
      if (settled) return;
      settled = true;
      result = value;
      if (dlg.open) dlg.close(); else cleanup();
    };

    const confirmNow = (): void => {
      const ok = cfg.okValue();
      if (ok === null) return; // walidacja nie przepuszcza
      finish(ok.value);
    };

    // Escape i `dlg.close()` z dowolnego miejsca → jedno wyjście z okienka.
    dlg.addEventListener('close', () => {
      settled = true;
      cleanup();
    });

    okBtn.addEventListener('click', confirmNow);
    if (cancelBtn) cancelBtn.addEventListener('click', () => finish(cfg.cancelValue));
    closeBtn.addEventListener('click', () => finish(cfg.cancelValue));

    // Klik w tło. `mousedown` pilnuje, żeby zaznaczanie tekstu wyciągnięte poza kartę
    // nie zamykało okienka.
    let downInside = false;
    dlg.addEventListener('mousedown', (e) => {
      downInside = card.contains(e.target as Node);
    });
    dlg.addEventListener('click', (e) => {
      if (downInside) return;
      if (!card.contains(e.target as Node)) finish(cfg.cancelValue);
    });

    dlg.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || e.isComposing || e.shiftKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target instanceof HTMLTextAreaElement) return; // nowa linia, nie potwierdzenie
      if (target === cancelBtn || target === closeBtn) return; // Enter = ten przycisk
      e.preventDefault();
      confirmNow();
    });

    dlg.showModal();
    const focusTarget = cfg.focusEl();
    if (focusTarget) {
      try { focusTarget.focus(); } catch (_e: unknown) { /* nieostre w starych silnikach */ }
      if (focusTarget instanceof HTMLInputElement || focusTarget instanceof HTMLTextAreaElement) {
        try { focusTarget.select(); } catch (_e: unknown) { /* number input nie zawsze umie */ }
      }
    }
  });
}

/**
 * Pytanie tak/nie w okienku SellBase. Zastępuje `confirm()`.
 * Escape, klik w tło i „×" oddają `false` — kod czekający na odpowiedź zawsze rusza dalej.
 */
export function showConfirm(title: string, message: string, opts?: SbConfirmOptions): Promise<boolean> {
  const o: SbConfirmOptions = opts || {};
  const tone = resolveTone(o);
  const shell = buildShell({
    title: title,
    message: message,
    tone: tone,
    okLabel: o.okLabel || tr('common.confirm', 'Potwierdź'),
    okI18nKey: o.okLabel ? null : 'common.confirm',
    cancelLabel: o.cancelLabel || tr('common.cancel', 'Anuluj'),
    cancelI18nKey: o.cancelLabel ? null : 'common.cancel',
    details: o.details,
    note: o.note,
  });
  return run<boolean>({
    shell: shell,
    cancelValue: false,
    okValue: () => ({ value: true }),
    // Przy akcji nieodwracalnej fokus startuje na „Anuluj" — przypadkowy Enter nic nie zepsuje.
    focusEl: () => ((tone === 'danger' || o.focusCancel) && shell.cancelBtn ? shell.cancelBtn : shell.okBtn),
  });
}

export interface SbChecklistItem {
  key: string;
  label: string;
  checked?: boolean;
}

export interface SbChecklistOptions extends SbConfirmOptions {
  /** Napis przycisku zależny od liczby zaznaczonych, np. (n) => `Wykonaj ${n}`. */
  okLabelFor?: (n: number) => string;
}

/**
 * S-48 (02.10.2026, audyt 30.09: „hurt nie pozwala odznaczyć pozycji”): lista do zaznaczenia w okienku SellBase.
 * Zwraca klucze zaznaczonych pozycji albo `null` przy anulowaniu; przy zerze zaznaczonych potwierdzenie jest zablokowane.
 */
export function showChecklist(title: string, message: string, items: SbChecklistItem[], opts?: SbChecklistOptions): Promise<string[] | null> {
  const o: SbChecklistOptions = opts || {};
  const fid = 'sb-dialog-lista-' + ++_fieldSeq;
  const label = (n: number): string => (o.okLabelFor ? o.okLabelFor(n) : (o.okLabel || tr('common.confirm', 'Potwierdź')));
  const fieldHtml =
    '<div class="sb-dialog-field sb-dialog-checklist" id="' + fid + '">' +
      '<div class="sb-dialog-checklist-bar">' +
        '<button type="button" class="btn-ghost btn-sm" data-sb-lista-wszystkie="1">Zaznacz wszystkie</button>' +
        '<button type="button" class="btn-ghost btn-sm" data-sb-lista-wszystkie="0">Odznacz wszystkie</button>' +
        '<span class="sb-dialog-checklist-count" aria-live="polite"></span>' +
      '</div>' +
      '<ul class="sb-dialog-checklist-items">' +
        items.map((it, i) =>
          '<li><label><input type="checkbox" data-sb-lista-klucz="' + escapeHtml(it.key) + '"' + (it.checked === false ? '' : ' checked') +
          ' id="' + fid + '-' + i + '"><span>' + escapeHtml(it.label) + '</span></label></li>').join('') +
      '</ul>' +
    '</div>';
  const shell = buildShell({
    title: title,
    message: message,
    tone: resolveTone(o),
    okLabel: label(items.filter((it) => it.checked !== false).length),
    okI18nKey: null,
    cancelLabel: o.cancelLabel || tr('common.cancel', 'Anuluj'),
    cancelI18nKey: o.cancelLabel ? null : 'common.cancel',
    details: o.details,
    note: o.note,
    fieldHtml: fieldHtml,
  });
  const boxes = (): HTMLInputElement[] => Array.from(shell.body.querySelectorAll<HTMLInputElement>('input[data-sb-lista-klucz]'));
  const odswiez = (): void => {
    const n = boxes().filter((b) => b.checked).length;
    shell.okBtn.textContent = label(n);
    shell.okBtn.disabled = n === 0;
    const licznik = shell.body.querySelector('.sb-dialog-checklist-count');
    if (licznik) licznik.textContent = `Zaznaczone: ${n} z ${items.length}`;
  };
  shell.body.addEventListener('change', odswiez);
  shell.body.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-sb-lista-wszystkie]');
    if (!b) return;
    boxes().forEach((x) => { x.checked = b.dataset.sbListaWszystkie === '1'; });
    odswiez();
  });
  odswiez();
  return run<string[] | null>({
    shell: shell,
    cancelValue: null,
    okValue: () => {
      const wybrane = boxes().filter((b) => b.checked).map((b) => String(b.dataset.sbListaKlucz || ''));
      return wybrane.length ? { value: wybrane } : null;
    },
    focusEl: () => shell.okBtn,
  });
}

/**
 * T-4 (audyt P1 03.10): wybór JEDNEJ pozycji z listy w okienku SellBase (np. powód odrzucenia z TikToka).
 * Zwraca klucz wybranej pozycji albo `null` przy anulowaniu; bez wyboru potwierdzenie jest zablokowane.
 */
export function showWybor(title: string, message: string, items: SbChecklistItem[], opts?: SbConfirmOptions): Promise<string | null> {
  const o: SbConfirmOptions = opts || {};
  const fid = 'sb-dialog-wybor-' + ++_fieldSeq;
  const fieldHtml =
    '<div class="sb-dialog-field sb-dialog-checklist" id="' + fid + '">' +
      '<ul class="sb-dialog-checklist-items" role="radiogroup">' +
        items.map((it, i) =>
          '<li><label><input type="radio" name="' + fid + '" data-sb-wybor-klucz="' + escapeHtml(it.key) + '"' +
          ' id="' + fid + '-' + i + '"><span>' + escapeHtml(it.label) + '</span></label></li>').join('') +
      '</ul>' +
    '</div>';
  const shell = buildShell({
    title: title,
    message: message,
    tone: resolveTone(o),
    okLabel: o.okLabel || tr('common.confirm', 'Potwierdź'),
    okI18nKey: o.okLabel ? null : 'common.confirm',
    cancelLabel: o.cancelLabel || tr('common.cancel', 'Anuluj'),
    cancelI18nKey: o.cancelLabel ? null : 'common.cancel',
    details: o.details,
    note: o.note,
    fieldHtml: fieldHtml,
  });
  const wybrany = (): HTMLInputElement | null => shell.body.querySelector<HTMLInputElement>('input[data-sb-wybor-klucz]:checked');
  const odswiez = (): void => { shell.okBtn.disabled = !wybrany(); };
  shell.body.addEventListener('change', odswiez);
  odswiez();
  return run<string | null>({
    shell: shell,
    cancelValue: null,
    okValue: () => {
      const w = wybrany();
      return w ? { value: String(w.dataset.sbWyborKlucz || '') } : null;
    },
    focusEl: () => shell.body.querySelector<HTMLInputElement>('input[data-sb-wybor-klucz]') || shell.okBtn,
  });
}

/**
 * Pytanie o wartość w okienku SellBase. Zastępuje `prompt()`.
 * Zwraca wpisany tekst albo `null` przy anulowaniu (dokładnie jak natywny `prompt`).
 */
export function showPrompt(title: string, message: string, opts?: SbPromptOptions): Promise<string | null> {
  const o: SbPromptOptions = opts || {};
  const tone = resolveTone(o);
  const type = o.type || 'text';
  const attrs: string[] = ['class="sb-dialog-input"', 'data-sb-dialog-input'];
  if (o.placeholder) attrs.push('placeholder="' + escapeHtml(o.placeholder) + '"');
  if (typeof o.maxlength === 'number') attrs.push('maxlength="' + o.maxlength + '"');
  if (type !== 'textarea') {
    attrs.push('type="' + type + '"');
    if (typeof o.min === 'number') attrs.push('min="' + o.min + '"');
    if (typeof o.max === 'number') attrs.push('max="' + o.max + '"');
    if (typeof o.step === 'number') attrs.push('step="' + o.step + '"');
  }
  const fieldId = 'sb-dialog-field-' + ++_fieldSeq;
  const labelHtml = o.label
    ? '<label class="sb-dialog-label" for="' + fieldId + '">' + escapeHtml(o.label) + '</label>'
    : '';
  const control = type === 'textarea'
    ? '<textarea id="' + fieldId + '" rows="4" ' + attrs.join(' ') + '></textarea>'
    : '<input id="' + fieldId + '" ' + attrs.join(' ') + '>';
  const fieldHtml =
    '<div class="sb-dialog-field">' + labelHtml + control +
    '<div class="sb-dialog-error" data-sb-dialog-error hidden></div></div>';

  const shell = buildShell({
    title: title,
    message: message,
    tone: tone,
    okLabel: o.okLabel || tr('common.confirm', 'Potwierdź'),
    okI18nKey: o.okLabel ? null : 'common.confirm',
    cancelLabel: o.cancelLabel || tr('common.cancel', 'Anuluj'),
    cancelI18nKey: o.cancelLabel ? null : 'common.cancel',
    details: o.details,
    note: o.note,
    fieldHtml: fieldHtml,
  });

  const input = shell.dlg.querySelector('[data-sb-dialog-input]') as HTMLInputElement | HTMLTextAreaElement;
  const errorEl = shell.dlg.querySelector('[data-sb-dialog-error]') as HTMLElement;
  if (typeof o.value === 'string') input.value = o.value;

  /** Komunikat błędu albo `null` gdy wartość jest do przyjęcia. */
  const problem = (): string | null => {
    const value = input.value;
    if (o.required && !value.trim()) return tr('common.field_required', 'To pole jest wymagane.');
    if (typeof o.validate === 'function') return o.validate(value);
    return null;
  };

  const paint = (msg: string | null): void => {
    if (msg) {
      errorEl.textContent = msg;
      errorEl.hidden = false;
      input.classList.add('is-invalid');
    } else {
      errorEl.textContent = '';
      errorEl.hidden = true;
      input.classList.remove('is-invalid');
    }
  };

  // Wymagane pole: przycisk zablokowany dopóki wartość nie przejdzie walidacji — bez straszenia
  // czerwienią zanim użytkownik cokolwiek napisze.
  const syncOk = (): void => {
    if (!o.required) return;
    shell.okBtn.disabled = problem() !== null;
  };
  syncOk();
  input.addEventListener('input', () => {
    if (!errorEl.hidden) paint(problem());
    syncOk();
  });

  return run<string | null>({
    shell: shell,
    cancelValue: null,
    okValue: () => {
      const msg = problem();
      if (msg) { paint(msg); input.focus(); return null; }
      paint(null);
      return { value: input.value };
    },
    focusEl: () => input,
  });
}

/**
 * Komunikat w okienku SellBase. Zastępuje `alert()` tam, gdzie trzeba przystanku
 * z jednym przyciskiem (zwykłe „coś poszło nie tak" idzie toastem, nie okienkiem).
 */
export function showAlert(title: string, message: string, opts?: SbAlertOptions): Promise<void> {
  const o: SbAlertOptions = opts || {};
  const shell = buildShell({
    title: title,
    message: message,
    tone: o.tone || 'default',
    okLabel: o.okLabel || tr('common.ok', 'OK'),
    okI18nKey: o.okLabel ? null : 'common.ok',
    cancelLabel: null,
    cancelI18nKey: null,
    details: o.details,
  });
  return run<void>({
    shell: shell,
    cancelValue: undefined,
    okValue: () => ({ value: undefined }),
    focusEl: () => shell.okBtn,
  }).then(() => undefined);
}

// Strangler jak w legacy-modals.ts: wystawiamy na window tylko gdy nikt nie zajął nazwy.
if (typeof window !== 'undefined') {
  const w = window as unknown as Record<string, unknown> & { __sbExports?: Record<string, unknown> };
  if (typeof w.showConfirm !== 'function') w.showConfirm = showConfirm;
  if (typeof w.showPrompt !== 'function') w.showPrompt = showPrompt;
  if (typeof w.showAlert !== 'function') w.showAlert = showAlert;
  if (!w.__sbExports) w.__sbExports = {};
  const ex = w.__sbExports;
  if (typeof ex.showConfirm !== 'function') ex.showConfirm = showConfirm;
  if (typeof ex.showPrompt !== 'function') ex.showPrompt = showPrompt;
  if (typeof ex.showAlert !== 'function') ex.showAlert = showAlert;
}
